import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Download,
  Building2,
  TrendingUp,
  Target,
  AlertTriangle,
  Zap,
  Map,
  DollarSign,
  Grid3X3,
  BarChart3,
  Loader2,
  ArrowLeft
} from 'lucide-react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  LineChart,
  Line
} from 'recharts';
import NavBar from '../components/NavBar';
import ReportChat from '../components/ReportChat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportDetailPage = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchReport();
  }, [id]);
  
  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/${id}`);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getPillarName = (pillar) => {
    const names = {
      digital_strategy: { tr: 'Dijital Strateji', en: 'Digital Strategy' },
      technology_infrastructure: { tr: 'Teknoloji', en: 'Technology' },
      data_analytics_ai: { tr: 'Veri & AI', en: 'Data & AI' },
      process_automation: { tr: 'Otomasyon', en: 'Automation' },
      customer_experience: { tr: 'Müşteri', en: 'Customer' },
      organization_culture: { tr: 'Kültür', en: 'Culture' }
    };
    return names[pillar]?.[language] || pillar;
  };
  
  const getBenchmarkLabel = (position) => {
    const labels = {
      digital_leader: { tr: 'Dijital Lider', en: 'Digital Leader', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
      above_average: { tr: 'Ortalamanın Üstünde', en: 'Above Average', color: 'text-blue-400', bg: 'bg-blue-500/10' },
      industry_average: { tr: 'Sektör Ortalaması', en: 'Industry Average', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
      below_average: { tr: 'Ortalamanın Altında', en: 'Below Average', color: 'text-orange-400', bg: 'bg-orange-500/10' },
      digitally_lagging: { tr: 'Dijital Geciken', en: 'Digitally Lagging', color: 'text-red-400', bg: 'bg-red-500/10' }
    };
    return labels[position] || { tr: position, en: position, color: 'text-slate-400', bg: 'bg-slate-500/10' };
  };
  
  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/reports/${id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GDMII_Report_${report.company?.name || 'Report'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert(language === 'tr' ? 'PDF indirilemedi. Lütfen tekrar deneyin.' : 'Failed to download PDF. Please try again.');
    }
  };
  
  const handleDownloadCSV = async () => {
    try {
      const response = await axios.get(`${API}/export/report/${id}/csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GDMII_Report_${report.company?.name || 'Report'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <NavBar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }
  
  if (!report) {
    return (
      <div className="min-h-screen bg-slate-950">
        <NavBar />
        <div className="pt-24 text-center text-white">
          {language === 'tr' ? 'Rapor bulunamadı' : 'Report not found'}
        </div>
      </div>
    );
  }
  
  // Prepare chart data
  const radarData = Object.entries(report.pillar_scores || {}).map(([pillar, score]) => ({
    pillar: getPillarName(pillar),
    score: score,
    fullMark: 100
  }));
  
  const benchmarkData = Object.entries(report.benchmark_comparison?.pillar_gaps || {}).map(([pillar, data]) => ({
    pillar: getPillarName(pillar),
    score: data.score,
    industry: data.industry_avg
  }));
  
  const priorityColors = { 1: '#10b981', 2: '#f59e0b', 3: '#ef4444' };
  
  return (
    <div className="min-h-screen bg-slate-950" data-testid="report-detail">
      <NavBar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex items-center space-x-4 min-w-0">
            <Button
              variant="ghost"
              onClick={() => navigate('/reports')}
              className="text-slate-400 hover:text-white shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-white break-words">{report.company?.name}</h1>
              <p className="text-slate-400">
                {language === 'tr' ? 'Dijital Olgunluk Raporu' : 'Digital Maturity Report'}
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleDownloadPDF}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="download-pdf-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              {t('downloadPDF')}
            </Button>
            <Button 
              onClick={handleDownloadCSV}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:text-white"
              data-testid="download-csv-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              CSV
            </Button>
          </div>
        </div>
        
        {/* Score Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 border-blue-500/30">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-slate-400 mb-2">GDMII Score</p>
              <p className="text-5xl font-bold text-white">{report.gdmii_score?.toFixed(1)}</p>
              <p className="text-slate-500">/100</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-slate-400 mb-2">
                {language === 'tr' ? 'Yüzdelik Dilim' : 'Percentile Rank'}
              </p>
              <p className="text-5xl font-bold text-white">{report.percentile_rank?.toFixed(0)}%</p>
              <p className="text-slate-500">{language === 'tr' ? 'Sektör içi' : 'In industry'}</p>
            </CardContent>
          </Card>
          
          <Card className={`border-slate-800 ${getBenchmarkLabel(report.benchmark_position).bg}`}>
            <CardContent className="p-6 text-center">
              <p className="text-sm text-slate-400 mb-2">
                {language === 'tr' ? 'Benchmark Pozisyonu' : 'Benchmark Position'}
              </p>
              <p className={`text-2xl font-bold ${getBenchmarkLabel(report.benchmark_position).color}`}>
                {getBenchmarkLabel(report.benchmark_position)[language]}
              </p>
            </CardContent>
          </Card>
        </div>
        
        {/* Radar Chart & Benchmark */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">
                {language === 'tr' ? 'Pillar Performansı' : 'Pillar Performance'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#334155" />
                    <PolarAngleAxis dataKey="pillar" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 10 }} />
                    <Radar
                      name="Score"
                      dataKey="score"
                      stroke="#3b82f6"
                      fill="#3b82f6"
                      fillOpacity={0.3}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">
                {language === 'tr' ? 'Sektör Karşılaştırması' : 'Industry Comparison'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={benchmarkData} layout="vertical">
                    <XAxis type="number" domain={[0, 100]} tick={{ fill: '#94a3b8' }} />
                    <YAxis type="category" dataKey="pillar" tick={{ fill: '#94a3b8', fontSize: 11 }} width={80} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="score" fill="#3b82f6" name={language === 'tr' ? 'Skorunuz' : 'Your Score'} />
                    <Bar dataKey="industry" fill="#64748b" name={language === 'tr' ? 'Sektör Ort.' : 'Industry Avg'} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Report Sections */}
        <Tabs defaultValue="summary" className="space-y-6">
          <TabsList className="bg-slate-900 border border-slate-800 p-1 flex flex-wrap w-full justify-start h-auto gap-1">
            <TabsTrigger value="summary" data-testid="tab-summary" className="text-xs sm:text-sm data-[state=active]:bg-blue-600">
              {t('executiveSummary')}
            </TabsTrigger>
            <TabsTrigger value="pillars" data-testid="tab-pillars" className="text-xs sm:text-sm data-[state=active]:bg-blue-600">
              {t('pillarAnalysis')}
            </TabsTrigger>
            <TabsTrigger value="gap" data-testid="tab-gap" className="text-xs sm:text-sm data-[state=active]:bg-blue-600">
              {t('gapAnalysis')}
            </TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai" className="text-xs sm:text-sm data-[state=active]:bg-blue-600">
              {t('aiReadiness')}
            </TabsTrigger>
            <TabsTrigger value="risk" data-testid="tab-risk" className="text-xs sm:text-sm data-[state=active]:bg-blue-600">
              {t('riskAnalysis')}
            </TabsTrigger>
            <TabsTrigger value="roadmap" data-testid="tab-roadmap" className="text-xs sm:text-sm data-[state=active]:bg-blue-600">
              {t('transformationRoadmap')}
            </TabsTrigger>
            <TabsTrigger value="roi" data-testid="tab-roi" className="text-xs sm:text-sm data-[state=active]:bg-blue-600">
              {t('roiSimulation')}
            </TabsTrigger>
            <TabsTrigger value="priority" data-testid="tab-priority" className="text-xs sm:text-sm data-[state=active]:bg-blue-600">
              {t('priorityMatrix')}
            </TabsTrigger>
            <TabsTrigger value="chat" data-testid="chat-tab-trigger" className="text-xs sm:text-sm data-[state=active]:bg-blue-600">
              {language === 'tr' ? 'AI Asistan' : 'AI Assistant'}
            </TabsTrigger>
          </TabsList>
          
          {/* AI Chat Assistant */}
          <TabsContent value="chat">
            <ReportChat reportId={id} />
          </TabsContent>
          
          {/* Executive Summary */}
          <TabsContent value="summary">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Building2 className="mr-2 h-5 w-5 text-blue-400" />
                  {t('executiveSummary')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-invert max-w-none">
                  <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">
                    {language === 'tr' ? report.executive_summary_tr : report.executive_summary_en}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Pillar Analysis */}
          <TabsContent value="pillars">
            <div className="space-y-4">
              {Object.entries(language === 'tr' ? (report.pillar_analysis_tr || {}) : (report.pillar_analysis_en || {})).map(([pillar, analysis]) => (
                <Card key={pillar} className="bg-slate-900/50 border-slate-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white">{getPillarName(pillar)}</CardTitle>
                      <span className="text-2xl font-bold text-blue-400">
                        {report.pillar_scores?.[pillar]?.toFixed(1)}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-300 whitespace-pre-wrap">{analysis}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          {/* Gap Analysis */}
          <TabsContent value="gap">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Target className="mr-2 h-5 w-5 text-amber-400" />
                  {t('gapAnalysis')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300 whitespace-pre-wrap">
                  {language === 'tr' ? report.gap_analysis_tr : report.gap_analysis_en}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* AI Readiness */}
          <TabsContent value="ai">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Zap className="mr-2 h-5 w-5 text-purple-400" />
                  {t('aiReadiness')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300 whitespace-pre-wrap">
                  {(language === 'tr' ? report.ai_readiness_tr : report.ai_readiness_en) || report.ai_readiness_tr || report.ai_readiness_en || (language === 'tr' ? 'Bu bölüm için içerik henüz hazırlanmadı. Raporu yeniden oluşturmayı deneyin.' : 'Content for this section is not available yet. Try regenerating the report.')}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Risk Analysis */}
          <TabsContent value="risk">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <AlertTriangle className="mr-2 h-5 w-5 text-red-400" />
                  {t('riskAnalysis')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300 whitespace-pre-wrap">
                  {language === 'tr' ? report.risk_analysis_tr : report.risk_analysis_en}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Transformation Roadmap */}
          <TabsContent value="roadmap">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Map className="mr-2 h-5 w-5 text-emerald-400" />
                  {t('transformationRoadmap')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300 whitespace-pre-wrap">
                  {language === 'tr' ? report.transformation_roadmap_tr : report.transformation_roadmap_en}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* ROI Simulation */}
          <TabsContent value="roi">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <DollarSign className="mr-2 h-5 w-5 text-green-400" />
                  {t('roiSimulation')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300 whitespace-pre-wrap">
                  {(language === 'tr' ? report.roi_simulation_tr : report.roi_simulation_en) || report.roi_simulation_tr || report.roi_simulation_en || (language === 'tr' ? 'Bu bölüm için içerik henüz hazırlanmadı. Raporu yeniden oluşturmayı deneyin.' : 'Content for this section is not available yet. Try regenerating the report.')}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Priority Matrix */}
          <TabsContent value="priority">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Grid3X3 className="mr-2 h-5 w-5 text-cyan-400" />
                  {t('priorityMatrix')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">
                          {language === 'tr' ? 'Alan' : 'Pillar'}
                        </th>
                        <th className="text-center py-3 px-4 text-slate-400 font-medium">
                          {language === 'tr' ? 'Skor' : 'Score'}
                        </th>
                        <th className="text-center py-3 px-4 text-slate-400 font-medium">
                          {language === 'tr' ? 'Etki' : 'Impact'}
                        </th>
                        <th className="text-center py-3 px-4 text-slate-400 font-medium">
                          {language === 'tr' ? 'Efor' : 'Effort'}
                        </th>
                        <th className="text-center py-3 px-4 text-slate-400 font-medium">
                          {language === 'tr' ? 'Öncelik' : 'Priority'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.priority_matrix?.map((item, index) => (
                        <tr key={index} className="border-b border-slate-800">
                          <td className="py-3 px-4 text-white">
                            {language === 'tr' ? item.pillar_name_tr : item.pillar_name_en}
                          </td>
                          <td className="py-3 px-4 text-center text-white font-medium">
                            {item.score}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              item.impact === 'high' ? 'bg-emerald-500/10 text-emerald-400' :
                              item.impact === 'medium' ? 'bg-amber-500/10 text-amber-400' :
                              'bg-slate-500/10 text-slate-400'
                            }`}>
                              {item.impact === 'high' ? (language === 'tr' ? 'Yüksek' : 'High') :
                               item.impact === 'medium' ? (language === 'tr' ? 'Orta' : 'Medium') :
                               (language === 'tr' ? 'Düşük' : 'Low')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              item.effort === 'low' ? 'bg-emerald-500/10 text-emerald-400' :
                              item.effort === 'medium' ? 'bg-amber-500/10 text-amber-400' :
                              'bg-red-500/10 text-red-400'
                            }`}>
                              {item.effort === 'high' ? (language === 'tr' ? 'Yüksek' : 'High') :
                               item.effort === 'medium' ? (language === 'tr' ? 'Orta' : 'Medium') :
                               (language === 'tr' ? 'Düşük' : 'Low')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span 
                              className="w-8 h-8 inline-flex items-center justify-center rounded-full text-white font-bold"
                              style={{ backgroundColor: priorityColors[item.priority] }}
                            >
                              P{item.priority}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ReportDetailPage;
